import { FabricImage, Canvas } from 'fabric'
import type { MediaLoadParams, MediaDimensions } from '../types'

/**
 * Параметры для расчета масштаба медиа
 */
export interface CalculateMediaScaleParams {
    mediaWidth: number
    mediaHeight: number
    canvasWidth: number
    canvasHeight: number
}

/**
 * Расчет базового масштаба медиа для размещения в canvas
 * @param params - параметры для расчета
 * @returns масштаб (0-1)
 */
export function calculateMediaScale(params: CalculateMediaScaleParams): number {
    const { mediaWidth, mediaHeight, canvasWidth, canvasHeight } = params

    // Если медиа меньше канваса по обеим осям, используем режим "cover"
    // (растягиваем на весь канвас с возможным обрезанием)
    // Это особенно важно для первых кадров видео, которые могут быть маленькими
    const isMediaSmallerThanCanvas =
        mediaWidth < canvasWidth && mediaHeight < canvasHeight

    if (isMediaSmallerThanCanvas) {
        // Режим "cover": заполняем весь канвас, медиа может обрезаться
        // Используем Math.max, чтобы медиа заполнило весь канвас
        return Math.max(canvasWidth / mediaWidth, canvasHeight / mediaHeight)
    }

    // Режим "fit": медиа полностью помещается в канвас без обрезания
    // Используем Math.min, чтобы медиа не обрезалось
    return Math.min(canvasWidth / mediaWidth, canvasHeight / mediaHeight)
}

/**
 * Параметры для позиционирования медиа на canvas
 */
export interface PositionMediaParams {
    image: FabricImage
    canvasWidth: number
    canvasHeight: number
}

/**
 * Позиционирование медиа на canvas (центрирование)
 * @param params - параметры для позиционирования
 */
export function positionMediaOnCanvas(params: PositionMediaParams): void {
    const { image, canvasWidth, canvasHeight } = params

    // Устанавливаем origin в центр для масштабирования
    image.set({
        originX: 'center',
        originY: 'center',
    })

    // Центрируем медиа на канвасе
    image.set({
        left: canvasWidth / 2,
        top: canvasHeight / 2,
        selectable: false, // Изображение не должно быть перемещаемым
        evented: false, // Отключаем события для изображения
    })
}

/**
 * Параметры для обновления медиа при изменении размеров canvas
 */
export interface UpdateMediaOnResizeParams {
    image: FabricImage
    canvasWidth: number
    canvasHeight: number
    originalWidth: number
    originalHeight: number
}

/**
 * Обновление медиа при изменении размеров canvas
 * @param params - параметры для обновления
 */
export function updateMediaOnResize(params: UpdateMediaOnResizeParams): void {
    const { image, canvasWidth, canvasHeight, originalWidth, originalHeight } =
        params

    // Если медиа меньше канваса по обеим осям, используем режим "cover"
    const isMediaSmallerThanCanvas =
        originalWidth < canvasWidth && originalHeight < canvasHeight

    // Рассчитываем базовый масштаб
    const baseScale = isMediaSmallerThanCanvas
        ? // Режим "cover": заполняем весь канвас
          Math.max(canvasWidth / originalWidth, canvasHeight / originalHeight)
        : // Режим "fit": медиа полностью помещается в канвас
          Math.min(canvasWidth / originalWidth, canvasHeight / originalHeight)

    // Применяем масштаб
    image.scale(baseScale)

    // Обновляем позицию
    image.set({
        left: canvasWidth / 2,
        top: canvasHeight / 2,
    })
}

/**
 * Типы ошибок загрузки медиа
 */
export class MediaLoadError extends Error {
    constructor(
        message: string,
        public readonly type:
            | 'no_media'
            | 'load_failed'
            | 'invalid_format'
            | 'unsupported_format'
            | 'unknown' = 'unknown',
        public readonly originalError?: unknown
    ) {
        super(message)
        this.name = 'MediaLoadError'
    }
}

/**
 * Загрузка изображения с обработкой CORS
 * @param imageUrl - URL изображения
 * @returns Promise с загруженным изображением
 * @throws MediaLoadError при ошибке загрузки
 */
export async function loadImage(imageUrl: string): Promise<FabricImage> {
    const isYandexStorage = imageUrl.includes('storage.yandexcloud.net')

    // Для Yandex Cloud Storage сразу загружаем без crossOrigin,
    // чтобы избежать ошибки CORS в консоли
    if (isYandexStorage) {
        try {
            const img = await FabricImage.fromURL(imageUrl)
            return img
        } catch (error) {
            throw new MediaLoadError(
                'Не удалось загрузить изображение из хранилища',
                'load_failed',
                error
            )
        }
    }

    // Для других источников пробуем сначала с crossOrigin
    try {
        const img = await FabricImage.fromURL(imageUrl, {
            crossOrigin: 'anonymous',
        })
        return img
    } catch (corsError) {
        // Если CORS не поддерживается, загружаем без crossOrigin
        // Это ограничит некоторые операции с canvas, но изображение загрузится
        try {
            const img = await FabricImage.fromURL(imageUrl)
            return img
        } catch (fallbackError) {
            // Определяем тип ошибки по сообщению
            const errorMessage =
                fallbackError instanceof Error
                    ? fallbackError.message.toLowerCase()
                    : String(fallbackError).toLowerCase()

            let errorType:
                | 'load_failed'
                | 'invalid_format'
                | 'unsupported_format' = 'load_failed'
            if (
                errorMessage.includes('format') ||
                errorMessage.includes('unsupported') ||
                errorMessage.includes('invalid')
            ) {
                errorType = 'unsupported_format'
            }

            throw new MediaLoadError(
                'Не удалось загрузить изображение. Возможно, формат не поддерживается.',
                errorType,
                fallbackError
            )
        }
    }
}

/**
 * Загрузка и размещение медиа на canvas
 * @param params - параметры для загрузки медиа
 * @returns Promise с загруженным изображением и его размерами
 * @throws MediaLoadError при ошибке загрузки или невалидных размерах
 */
export async function loadAndPositionMedia(
    params: MediaLoadParams
): Promise<{ image: FabricImage; dimensions: MediaDimensions }> {
    const { imageUrl, fabricCanvas, canvasWidth, canvasHeight } = params

    if (!imageUrl) {
        throw new MediaLoadError('URL изображения не указан', 'no_media')
    }

    // Загружаем изображение
    let img: FabricImage
    try {
        img = await loadImage(imageUrl)
    } catch (error) {
        // Если это уже MediaLoadError, пробрасываем дальше
        if (error instanceof MediaLoadError) {
            throw error
        }
        // Иначе оборачиваем в MediaLoadError
        throw new MediaLoadError(
            'Не удалось загрузить изображение',
            'load_failed',
            error
        )
    }

    // Получаем оригинальные размеры изображения
    const originalWidth = img.width ?? 0
    const originalHeight = img.height ?? 0

    if (originalWidth === 0 || originalHeight === 0) {
        throw new MediaLoadError(
            'Невалидные размеры изображения. Возможно, формат не поддерживается.',
            'invalid_format'
        )
    }

    const dimensions: MediaDimensions = {
        width: originalWidth,
        height: originalHeight,
    }

    // Рассчитываем базовый масштаб
    const baseScale = calculateMediaScale({
        mediaWidth: originalWidth,
        mediaHeight: originalHeight,
        canvasWidth,
        canvasHeight,
    })

    // Применяем масштаб
    img.scale(baseScale)

    // Позиционируем медиа
    positionMediaOnCanvas({
        image: img,
        canvasWidth,
        canvasHeight,
    })

    // Удаляем старое изображение, если оно есть
    const existingImages = fabricCanvas
        .getObjects()
        .filter((obj) => obj.type === 'image')
    existingImages.forEach((obj) => fabricCanvas.remove(obj))

    // Добавляем изображение на canvas
    fabricCanvas.add(img)
    // Отправляем изображение на задний план (за текстом)
    fabricCanvas.sendObjectToBack(img)

    return { image: img, dimensions }
}
